import React, { useEffect, useRef } from 'react';
import { Building2, CheckCircle, Landmark, ChevronRight, MapPin, Wrench, Users2 } from 'lucide-react';

const useScrollAnimation = (threshold = 0.08) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// ── All clients for logo grid ──────────────────────────────────────────────
const allClients = [
  {
    name: 'Grupo Salinas',
    abbr: 'GS',
    accentColor: '#C41E3A',
    sector: 'Corporativo Nacional',
    note: 'Empresa matriz',
  },
  {
    name: 'Elektra',
    abbr: 'EK',
    accentColor: '#F5A623',
    sector: 'Retail & Comercio',
    note: 'Sucursales en Oaxaca',
  },
  {
    name: 'Banco Azteca',
    abbr: 'BA',
    accentColor: '#004B93',
    sector: 'Servicios Financieros',
    note: 'Adecuaciones y acabados',
  },
  {
    name: 'Farmacias del Ahorro',
    abbr: 'FA',
    accentColor: '#E31E24',
    sector: 'Salud & Farmacia',
    note: 'Llave en mano',
  },
  {
    name: 'INFONAVIT',
    abbr: 'INF',
    accentColor: '#1565C0',
    sector: 'Vivienda Federal',
    note: 'Ejercicios 2016–2025',
    isGov: true,
  },
  {
    name: 'FOVISSSTE',
    abbr: 'FOV',
    accentColor: '#2E7D32',
    sector: 'Vivienda Federal',
    note: 'Ejercicios 2018–2025',
    isGov: true,
  },
  {
    name: 'IOCIFED',
    abbr: 'IOC',
    accentColor: '#7C3AED',
    sector: 'Infraestructura Educativa',
    note: 'Obra educativa estatal',
    isGov: true,
  },
  {
    name: 'Vivienda Bienestar',
    abbr: 'VB',
    accentColor: '#374151',
    sector: 'Gobierno Federal',
    note: 'Programa federal',
    isGov: true,
  },
];

// ── Featured clients with project photos ───────────────────────────────────
const featuredClients = [
  {
    headline: 'Construcción de sucursales Elektra y Banco Azteca',
    client: 'Grupo Salinas',
    workType: 'Construcción y remodelación',
    location: 'Oaxaca de Juárez, Oax.',
    name: 'Elektra / Banco Azteca',
    category: 'Retail & Servicios Financieros',
    description: 'Proyecto comercial para Grupo Salinas: edificación y adecuación de sucursales en la región de Oaxaca, con apego total a los estándares de imagen corporativa de la cadena.',
    scope: [
      'Obra civil y estructura de concreto',
      'Fachada panelada con identidad corporativa Elektra',
      'Adecuación de interiores por área de servicio',
      'Pavimentación y accesos vehiculares',
      'Instalaciones eléctricas y especiales',
    ],
    photo: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/r5pinbmh_WhatsApp%20Image%202022-12-30%20at%205.23.39%20PM.jpeg',
    tags: ['Obra Comercial', 'Construcción', 'Fachada'],
    accentColor: '#F5A623',
  },
  {
    headline: 'Edificación llave en mano – Farmacias del Ahorro',
    client: 'Farmacias del Ahorro',
    workType: 'Construcción llave en mano',
    location: 'Oaxaca, Oax.',
    name: 'Farmacias del Ahorro',
    category: 'Salud & Farmacéutica',
    description: 'Construcción integral de sucursales farmacéuticas desde cimentación hasta entrega final, cumpliendo con las especificaciones técnicas y de imagen de la cadena nacional.',
    scope: [
      'Cimentación y estructura completa',
      'Fachada e imagen corporativa de cadena',
      'Interiores con señalética y acabados especializados',
      'Instalaciones hidrosanitarias y eléctricas',
      'Entrega llave en mano con garantía',
    ],
    photo: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/gr5c56ew_WhatsApp%20Image%202026-01-06%20at%202.5.jpeg',
    tags: ['Llave en Mano', 'Construcción', 'Acabados'],
    accentColor: '#E31E24',
  },
  {
    headline: 'Adecuación y remodelación – Tiendas Neto',
    client: 'Tiendas Neto / Grupo Salinas',
    workType: 'Remodelación y adecuación',
    location: 'Valles Centrales, Oaxaca',
    name: 'Tiendas Neto',
    category: 'Retail & Abarrotes',
    description: 'Remodelación y adecuación de tiendas de autoservicio bajo los lineamientos técnicos de Tiendas Neto, con cumplimiento de plazos y estándares de calidad de la cadena.',
    scope: [
      'Demolición y adecuación de espacios comerciales',
      'Remodelación de fachada y accesos',
      'Instalación de pisos, plafones y acabados',
      'Adecuaciones eléctricas e hidráulicas',
      'Apego a manuales de imagen corporativa',
    ],
    photo: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/gljs3ywz_WhatsApp%20Image%202026-04-02%20at%2011.19.17%20AM.jpeg',
    tags: ['Remodelación', 'Adecuación', 'Retail'],
    accentColor: '#1B5E20',
  },
];

// ── Public sector dependencies ─────────────────────────────────────────────
const govDependencies = [
  'CEVI – Comisión Estatal de Vivienda e Infraestructura',
  'SINFRA – Secretaría de Infraestructura de Oaxaca',
  'SCT – Secretaría de Comunicaciones y Transportes',
  'Programa Federal Vivienda Bienestar',
];

const Clients = () => {
  const ref = useScrollAnimation();

  return (
    <section
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--bg-primary)',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid var(--border-color)',
      }}
    >
      {/* Top red bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--red)' }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto' }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label">Prueba social</div>
          <h2 style={{
            fontFamily: 'var(--font-heading)', fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', marginTop: '0.5rem',
          }}>
            Clientes y proyectos{' '}
            <span style={{ color: 'var(--red)' }}>destacados</span>
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '1.05rem',
            color: 'var(--gray)', maxWidth: 620, margin: '1rem auto 0', lineHeight: 1.75,
          }}>
            Gobierno federal y estatal, Grupo Salinas (Elektra, Banco Azteca, Tiendas Neto),
            sector farmacéutico y clientes particulares en todo el estado de Oaxaca.
          </p>
        </div>

        {/* ── LOGO / NAME GRID ───────────────────────────────────────────── */}
        <div className="fade-up" style={{ marginBottom: '4.5rem' }}>
          <p style={{
            fontFamily: 'var(--font-heading)', fontWeight: 700,
            fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--gray)', textAlign: 'center', marginBottom: '1.75rem',
          }}>
            Empresas e instituciones que nos eligen
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
          }} className="logo-grid">
            {allClients.map((client, i) => (
              <div
                key={i}
                className={`scale-in delay-${i + 1}`}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 14,
                  padding: '1.25rem 0.875rem 1rem',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = client.accentColor + '50';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                {/* Top accent bar */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 3, background: client.accentColor, borderRadius: '14px 14px 0 0',
                }} />

                {/* Abbr / Logo placeholder */}
                <div style={{
                  width: 48, height: 48, borderRadius: 10,
                  background: client.accentColor + '15',
                  border: `1.5px solid ${client.accentColor}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                }}>
                  {client.isGov ? (
                    <Landmark size={20} color={client.accentColor} strokeWidth={1.75} />
                  ) : (
                    <span style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 900,
                      fontSize: client.abbr.length > 2 ? '0.65rem' : '0.85rem',
                      color: client.accentColor, letterSpacing: '0.04em',
                    }}>
                      {client.abbr}
                    </span>
                  )}
                </div>

                <div style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 800,
                  fontSize: '0.78rem', color: 'var(--text-primary)',
                  lineHeight: 1.3, marginBottom: '0.3rem',
                }}>
                  {client.name}
                </div>
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.62rem',
                  color: 'var(--gray)', letterSpacing: '0.04em',
                }}>
                  {client.sector}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── GOVERNMENT / PUBLIC SECTOR CALLOUT ─────────────────────────── */}
        <div className="fade-up" style={{ marginBottom: '4.5rem' }}>
          <div style={{
            background: 'var(--band-bg)',
            boxShadow: 'inset 0 0 0 1px var(--band-ring)',
            borderRadius: 18,
            padding: '2.5rem 2.75rem',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '2rem',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }} className="gov-block">
            {/* BG texture */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(196,30,58,0.15) 0%, transparent 60%)',
              pointerEvents: 'none',
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
                <div style={{
                  width: 36, height: 36, background: 'rgba(196,30,58,0.2)',
                  borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Landmark size={18} color="var(--red)" strokeWidth={1.75} />
                </div>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: 'var(--red)',
                }}>
                  Sector Público
                </span>
              </div>

              <h3 style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', color: 'var(--band-text)',
                lineHeight: 1.25, marginBottom: '0.75rem',
              }}>
                Experiencia en obra pública<br />
                <span style={{ color: 'var(--band-text-55)', fontWeight: 600, fontSize: '0.85em' }}>
                  para dependencias estatales y federales en Oaxaca
                </span>
              </h3>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {govDependencies.map((dep, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    background: 'var(--band-surface-hover)', border: '1px solid var(--band-border-strong)',
                    color: 'var(--band-text-80)',
                    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '0.78rem',
                    padding: '0.35rem 0.7rem', borderRadius: 20,
                  }}>
                    <CheckCircle size={10} color="var(--red)" strokeWidth={2.5} />
                    {dep}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ position: 'relative', zIndex: 1, flexShrink: 0, textAlign: 'center' }}>
              <div style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: '3rem', color: 'var(--band-text)', lineHeight: 1,
              }}>
                200<span style={{ color: 'var(--red)' }}>+</span>
              </div>
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                color: 'var(--band-text-55)', marginTop: '0.4rem', letterSpacing: '0.04em',
              }}>
                proyectos<br />entregados
              </div>
            </div>
          </div>
        </div>

        {/* ── FEATURED CLIENT CARDS (with photos) ────────────────────────── */}
        <div className="fade-up" style={{ marginBottom: '1rem' }}>
          <p style={{
            fontFamily: 'var(--font-heading)', fontWeight: 700,
            fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--gray)', marginBottom: '1.75rem',
          }}>
            Proyectos comerciales recientes
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.75rem',
        }} className="clients-grid">
          {featuredClients.map((client, i) => (
            <div
              key={i}
              className={`scale-in delay-${i + 1}`}
              style={{
                background: 'var(--bg-card)',
                borderRadius: 16,
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.12)';
                e.currentTarget.style.borderColor = 'rgba(196,30,58,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.06)';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
            >
              {/* Photo */}
              <div style={{ height: 220, position: 'relative', overflow: 'hidden', background: 'var(--gray-light)' }}>
                <img
                  src={client.photo}
                  alt={client.name}
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    objectPosition: 'center top',
                    transition: 'transform 0.6s ease',
                  }}
                  onMouseEnter={(e) => (e.target.style.transform = 'scale(1.06)')}
                  onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, var(--bg-card) 0%, transparent 55%)',
                  pointerEvents: 'none',
                }} />
                {/* Category badge */}
                <div style={{
                  position: 'absolute', top: '0.875rem', left: '0.875rem',
                  background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-heading)', fontWeight: 600,
                  fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                  padding: '0.3rem 0.65rem', borderRadius: 4,
                  border: '1px solid var(--border-color)',
                }}>
                  {client.category}
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '1.5rem 1.75rem 2rem' }}>
                {/* Red accent */}
                <div style={{ width: 36, height: 3, background: 'var(--red)', borderRadius: 2, marginBottom: '0.875rem' }} />

                {/* Headline */}
                <h3 style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 800,
                  fontSize: '0.975rem', color: 'var(--text-primary)',
                  lineHeight: 1.35, marginBottom: '0.875rem',
                }}>
                  {client.headline}
                </h3>

                {/* Meta row: client · work type · location */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Users2 size={11} color="var(--red)" strokeWidth={2} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--gray)' }}>
                      <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Cliente:</strong>{' '}{client.client}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Wrench size={11} color="var(--red)" strokeWidth={2} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--gray)' }}>
                      <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Tipo de obra:</strong>{' '}{client.workType}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <MapPin size={11} color="var(--red)" strokeWidth={2} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--gray)' }}>
                      <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Ubicación:</strong>{' '}{client.location}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'var(--border-color)', marginBottom: '1rem' }} />

                {/* Scope list */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 700,
                    fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'var(--gray)', marginBottom: '0.6rem',
                  }}>
                    Alcance del proyecto
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {client.scope.map((item, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <CheckCircle size={13} color="var(--red)" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--gray-dark)', lineHeight: 1.5 }}>
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {client.tags.map((tag, j) => (
                    <span key={j} style={{
                      background: 'rgba(196,30,58,0.07)',
                      border: '1px solid rgba(196,30,58,0.15)',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-heading)', fontWeight: 600,
                      fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                      padding: '0.25rem 0.6rem', borderRadius: 4,
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── BOTTOM CTA STRIP ───────────────────────────────────────────── */}
        <div className="fade-up" style={{
          marginTop: '4rem',
          padding: '2rem 2.5rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: 44, height: 44, background: 'var(--red)',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Building2 size={22} color="white" strokeWidth={1.5} />
            </div>
            <div>
              <div style={{
                fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '1rem', color: 'var(--text-primary)',
              }}>
                ¿Tu empresa necesita construir o remodelar?
              </div>
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: '0.85rem',
                color: 'var(--gray)', marginTop: 2,
              }}>
                Trabajamos con cadenas comerciales, gobierno y clientes particulares en todo el estado.
              </div>
            </div>
          </div>
          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-red"
            style={{
              whiteSpace: 'nowrap', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}
          >
            Solicitar cotización <ChevronRight size={16} />
          </button>
        </div>

      </div>

      <style>{`
        @media (max-width: 1050px) {
          .logo-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
        @media (max-width: 900px) {
          .clients-grid { grid-template-columns: 1fr !important; }
          .gov-block { grid-template-columns: 1fr !important; }
          .logo-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .logo-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
};

export default Clients;
